import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Settings, User, Shield, Bell, Key, Plug, 
  Mail, Phone, MapPin, Camera, Lock, Clock, 
  Globe, Eye, EyeOff, CheckCircle, AlertTriangle,
  Users, Database, Zap, FileText, MessageSquare, Monitor
} from 'lucide-react';
import { useNotificationSettings, useSecuritySettings } from '../../../stores/useSettingsStore';
import toast from 'react-hot-toast';

interface LaunchPadSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string;
  userRole?: 'client' | 'vendor' | 'admin';
}

type SettingsTab = 'profile' | 'authentication' | 'notifications' | 'security' | 'integrations' | 'executive-assistance' | 'administration';

const tabs: Array<{
  id: SettingsTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}> = [
  { 
    id: 'profile', 
    label: 'Profile', 
    icon: User,
    description: 'Personal information and preferences'
  },
  { 
    id: 'authentication', 
    label: 'Authentication', 
    icon: Shield,
    description: 'Sign-in methods and security'
  },
  { 
    id: 'notifications', 
    label: 'Notifications', 
    icon: Bell,
    description: 'Communication preferences'
  },
  { 
    id: 'security', 
    label: 'Security', 
    icon: Lock,
    description: 'Access control and permissions'
  },
  { 
    id: 'integrations', 
    label: 'Integrations', 
    icon: Plug,
    description: 'Connected services and APIs'
  },
  { 
    id: 'executive-assistance', 
    label: 'Executive Assistant', 
    icon: Zap,
    description: 'AI-powered assistance settings'
  },
  { 
    id: 'administration', 
    label: 'Administration', 
    icon: Users,
    description: 'System and user management'
  }
];

const LaunchPadSettings: React.FC<LaunchPadSettingsProps> = ({ 
  isOpen, 
  onClose, 
  projectId,
  userRole = 'client'
}) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [profileData, setProfileData] = useState({
    firstName: 'John',
    lastName: 'Smith',
    email: 'john.smith@tylercounty.gov',
    phone: '+1 (555) 123-4567',
    location: 'Tyler County, TX',
    role: 'Project Manager',
    department: 'IT Services'
  });
  
  const [authSettings, setAuthSettings] = useState({
    mfaEnabled: true,
    ssoEnabled: true,
    sessionTimeout: 60,
    allowedOrigins: ['https://tyler.com', 'https://app.tyler.com']
  });

  const [showApiKeys, setShowApiKeys] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  
  const { notifications, setNotifications } = useNotificationSettings();
  const { security, setSecurity } = useSecuritySettings();

  const handleMfaToggle = async () => {
    setLoading(true);
    try {
      setAuthSettings(prev => ({ ...prev, mfaEnabled: !prev.mfaEnabled }));
      setSecurity({ mfaEnabled: !authSettings.mfaEnabled });
      toast.success(`Two-factor authentication ${!authSettings.mfaEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating MFA settings:', error);
      toast.error('Failed to update two-factor authentication');
    } finally {
      setLoading(false);
    }
  };

  const handleSessionTimeoutChange = async (timeout: number) => {
    try {
      setAuthSettings(prev => ({ ...prev, sessionTimeout: timeout }));
      setSecurity({ sessionTimeout: timeout });
      toast.success('Session timeout updated');
    } catch (error) {
      console.error('Error updating session timeout:', error);
      toast.error('Failed to update session timeout');
    }
  };

  const toggleNotification = async (type: keyof typeof notifications) => {
    setLoading(true);
    try {
      setNotifications({ [type]: !notifications[type] });
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} notifications ${!notifications[type] ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating notification settings:', error);
      toast.error('Failed to update notification settings');
    } finally {
      setLoading(false);
    }
  };

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-white drop-shadow-sm mb-2">Profile Information</h3>
        <p className="text-slate-300 drop-shadow-sm">Manage your personal information and account details</p>
      </div>

      <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
        <div className="flex items-center space-x-6 mb-6">
          <div className="relative">
            <div className="w-20 h-20 bg-gradient-to-br from-[#4267A1] to-[#5A7BC4] rounded-full flex items-center justify-center shadow-lg">
              <User size={32} className="text-white drop-shadow-sm" />
            </div>
            <button className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-r from-[#8A9150] to-[#A5B366] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200">
              <Camera size={14} className="text-white" />
            </button>
          </div>
          <div>
            <h4 className="text-xl font-semibold text-white drop-shadow-sm">{profileData.firstName} {profileData.lastName}</h4>
            <p className="text-slate-300 drop-shadow-sm">{profileData.role}</p>
            <p className="text-sm text-slate-400">{profileData.department}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-white drop-shadow-sm mb-2">First Name</label>
            <input
              type="text"
              value={profileData.firstName}
              onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
              className="w-full p-3 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-[#4267A1] focus:shadow-lg focus:shadow-[#4267A1]/25 transition-all duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white drop-shadow-sm mb-2">Last Name</label>
            <input
              type="text"
              value={profileData.lastName}
              onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
              className="w-full p-3 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-[#4267A1] focus:shadow-lg focus:shadow-[#4267A1]/25 transition-all duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white drop-shadow-sm mb-2">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                className="w-full pl-10 p-3 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-[#4267A1] focus:shadow-lg focus:shadow-[#4267A1]/25 transition-all duration-200"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-white drop-shadow-sm mb-2">Phone Number</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                className="w-full pl-10 p-3 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-[#4267A1] focus:shadow-lg focus:shadow-[#4267A1]/25 transition-all duration-200"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button className="px-6 py-3 bg-slate-700/50 border border-slate-600/30 hover:bg-slate-600/50 text-white rounded-lg transition-all duration-200 font-medium drop-shadow-sm">
            Cancel
          </button>
          <button className="px-6 py-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 font-medium drop-shadow-sm">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );

  const renderAuthenticationTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-white drop-shadow-sm mb-2">Authentication Methods</h3>
        <p className="text-slate-300 drop-shadow-sm">Manage your sign-in methods and security preferences</p>
      </div>

      <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="w-6 h-6 text-[#4267A1] drop-shadow-sm" />
          <h4 className="text-lg font-semibold text-white drop-shadow-sm">Email Address</h4>
        </div>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-white drop-shadow-sm">{profileData.email}</p>
            <p className="text-sm text-slate-400 drop-shadow-sm">Primary sign-in method</p>
          </div>
          <button className="px-4 py-2 bg-slate-700/50 border border-slate-600/30 hover:bg-slate-600/50 text-[#4267A1] rounded-lg transition-all duration-200 font-medium drop-shadow-sm">
            Change Email
          </button>
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          <Lock className="w-6 h-6 text-[#8A9150] drop-shadow-sm" />
          <h4 className="text-lg font-semibold text-white drop-shadow-sm">Password</h4>
        </div>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-white drop-shadow-sm">••••••••••••••••</p>
            <p className="text-sm text-slate-400 drop-shadow-sm">Last updated 30 days ago</p>
          </div>
          <button className="px-4 py-2 bg-slate-700/50 border border-slate-600/30 hover:bg-slate-600/50 text-[#8A9150] rounded-lg transition-all duration-200 font-medium drop-shadow-sm">
            Change Password
          </button>
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-6 h-6 text-[#5A7BC4] drop-shadow-sm" />
          <h4 className="text-lg font-semibold text-white drop-shadow-sm">Two-Factor Authentication</h4>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white drop-shadow-sm">Secure Your Account</p>
            <p className="text-sm text-slate-400 drop-shadow-sm">Add an extra layer of security with 2FA</p>
          </div>
          <button
            onClick={() => setAuthSettings({...authSettings, mfaEnabled: !authSettings.mfaEnabled})}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              authSettings.mfaEnabled ? 'bg-gradient-to-r from-[#8A9150] to-[#A5B366]' : 'bg-gradient-to-r from-[#4267A1]/60 to-[#5A7BC4]/60'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-lg ${
                authSettings.mfaEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {authSettings.mfaEnabled && (
          <div className="mt-4 p-4 bg-[#8A9150]/10 border border-[#8A9150]/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-[#8A9150] drop-shadow-sm" />
              <span className="text-sm text-[#8A9150] font-medium drop-shadow-sm">Two-factor authentication is enabled</span>
            </div>
            <button className="px-4 py-2 bg-gradient-to-r from-[#8A9150] to-[#A5B366] text-white rounded-lg hover:shadow-lg hover:shadow-[#8A9150]/25 transition-all duration-200 font-medium text-sm drop-shadow-sm">
              View Recovery Codes
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderExecutiveAssistanceTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-white drop-shadow-sm mb-2">Executive Assistant Settings</h3>
        <p className="text-slate-300 drop-shadow-sm">Configure AI-powered assistance and automation preferences</p>
      </div>

      <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          <Zap className="w-6 h-6 text-[#4267A1] drop-shadow-sm" />
          <h4 className="text-lg font-semibold text-white drop-shadow-sm">AI Assistant Preferences</h4>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white drop-shadow-sm">Smart Task Prioritization</p>
              <p className="text-sm text-slate-400 drop-shadow-sm">Automatically prioritize tasks based on deadlines and importance</p>
            </div>
            <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-gradient-to-r from-[#4267A1] to-[#5A7BC4]">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-lg translate-x-6" />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white drop-shadow-sm">Proactive Notifications</p>
              <p className="text-sm text-slate-400 drop-shadow-sm">Receive intelligent alerts for upcoming deadlines and blockers</p>
            </div>
            <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-gradient-to-r from-[#8A9150] to-[#A5B366]">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-lg translate-x-6" />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white drop-shadow-sm">Meeting Scheduling</p>
              <p className="text-sm text-slate-400 drop-shadow-sm">Allow AI to schedule meetings and coordinate calendars</p>
            </div>
            <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-gradient-to-r from-[#4267A1]/60 to-[#5A7BC4]/60">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-lg translate-x-1" />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          <Database className="w-6 h-6 text-[#5A7BC4] drop-shadow-sm" />
          <h4 className="text-lg font-semibold text-white drop-shadow-sm">Data Access Permissions</h4>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-slate-700/50 border border-slate-600/30 rounded-lg">
            <span className="text-white drop-shadow-sm">Project Data</span>
            <span className="px-3 py-1 bg-[#8A9150]/20 text-[#8A9150] rounded-full text-sm font-medium drop-shadow-sm">Full Access</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-700/50 border border-slate-600/30 rounded-lg">
            <span className="text-white drop-shadow-sm">Calendar Integration</span>
            <span className="px-3 py-1 bg-[#4267A1]/20 text-[#4267A1] rounded-full text-sm font-medium drop-shadow-sm">Read Only</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-700/50 border border-slate-600/30 rounded-lg">
            <span className="text-white drop-shadow-sm">Email Integration</span>
            <span className="px-3 py-1 bg-slate-600/20 text-slate-400 rounded-full text-sm font-medium drop-shadow-sm">Disabled</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdministrationTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-white drop-shadow-sm mb-2">System Administration</h3>
        <p className="text-slate-300 drop-shadow-sm">Manage users, permissions, and system settings</p>
      </div>

      {userRole === 'admin' ? (
        <>
          <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <Users className="w-6 h-6 text-[#4267A1] drop-shadow-sm" />
              <h4 className="text-lg font-semibold text-white drop-shadow-sm">User Management</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-slate-700/50 border border-slate-600/30 rounded-lg text-center">
                <div className="text-2xl font-bold text-[#4267A1] drop-shadow-sm">24</div>
                <div className="text-sm text-slate-400 drop-shadow-sm">Active Users</div>
              </div>
              <div className="p-4 bg-slate-700/50 border border-slate-600/30 rounded-lg text-center">
                <div className="text-2xl font-bold text-[#8A9150] drop-shadow-sm">8</div>
                <div className="text-sm text-slate-400 drop-shadow-sm">Pending Invites</div>
              </div>
              <div className="p-4 bg-slate-700/50 border border-slate-600/30 rounded-lg text-center">
                <div className="text-2xl font-bold text-[#5A7BC4] drop-shadow-sm">3</div>
                <div className="text-sm text-slate-400 drop-shadow-sm">Admin Users</div>
              </div>
            </div>
            
            <button className="px-6 py-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 font-medium drop-shadow-sm">
              Manage Users
            </button>
          </div>

          <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-6 h-6 text-[#8A9150] drop-shadow-sm" />
              <h4 className="text-lg font-semibold text-white drop-shadow-sm">Security Audit</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-700/50 border border-slate-600/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#8A9150] drop-shadow-sm" />
                  <span className="text-white drop-shadow-sm">SSL Certificate Valid</span>
                </div>
                <span className="text-sm text-slate-400 drop-shadow-sm">Expires Dec 2024</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-700/50 border border-slate-600/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#8A9150] drop-shadow-sm" />
                  <span className="text-white drop-shadow-sm">Database Encrypted</span>
                </div>
                <span className="text-sm text-slate-400 drop-shadow-sm">AES-256</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-700/50 border border-slate-600/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 drop-shadow-sm" />
                  <span className="text-white drop-shadow-sm">Password Policy</span>
                </div>
                <span className="text-sm text-yellow-500 drop-shadow-sm">Needs Review</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-8 text-center">
          <Shield className="w-12 h-12 text-slate-400 mx-auto mb-4 drop-shadow-sm" />
          <h4 className="text-lg font-semibold text-white drop-shadow-sm mb-2">Administrative Access Required</h4>
          <p className="text-slate-400 drop-shadow-sm">You need administrator privileges to access system administration settings.</p>
        </div>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileTab();
      case 'authentication':
        return renderAuthenticationTab();
      case 'notifications':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-white drop-shadow-sm mb-2">Notification Preferences</h3>
              <p className="text-slate-300 drop-shadow-sm">Control how and when you receive notifications</p>
            </div>

            {/* Email Notifications */}
            <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-5 h-5 text-[#4267A1] drop-shadow-sm" />
                <h4 className="text-base font-medium text-white drop-shadow-sm">Email Notifications</h4>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-300 drop-shadow-sm">Email alerts</p>
                  <p className="text-xs text-slate-500 drop-shadow-sm">
                    Receive email notifications for task completions and important updates
                  </p>
                </div>
                <button
                  onClick={() => toggleNotification('email')}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    notifications.email ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4]' : 'bg-gradient-to-r from-[#4267A1]/60 to-[#5A7BC4]/60'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-lg ${
                      notifications.email ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Slack Notifications */}
            <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <MessageSquare className="w-5 h-5 text-[#8A9150] drop-shadow-sm" />
                <h4 className="text-base font-medium text-white drop-shadow-sm">Slack Integration</h4>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-300 drop-shadow-sm">Slack notifications</p>
                  <p className="text-xs text-slate-500 drop-shadow-sm">
                    Send notifications to your Slack workspace when tasks complete
                  </p>
                </div>
                <button
                  onClick={() => toggleNotification('slack')}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    notifications.slack ? 'bg-gradient-to-r from-[#8A9150] to-[#A5B366]' : 'bg-gradient-to-r from-[#4267A1]/60 to-[#5A7BC4]/60'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-lg ${
                      notifications.slack ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {notifications.slack && (
                <div className="mt-4 p-4 bg-slate-700 rounded-lg">
                  <label className="block text-sm font-medium text-slate-300 mb-2 drop-shadow-sm">
                    Slack Webhook URL
                  </label>
                  <input
                    type="url"
                    placeholder="https://hooks.slack.com/services/..."
                    className="w-full p-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm focus:border-[#8A9150] focus:ring-1 focus:ring-[#8A9150] transition-colors"
                  />
                  <p className="text-xs text-slate-500 mt-1 drop-shadow-sm">
                    Enter your Slack webhook URL to receive notifications
                  </p>
                </div>
              )}
            </div>

            {/* SMS Notifications */}
            <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Phone className="w-5 h-5 text-orange-400 drop-shadow-sm" />
                <h4 className="text-base font-medium text-white drop-shadow-sm">SMS Notifications</h4>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-300 drop-shadow-sm">Text message alerts</p>
                  <p className="text-xs text-slate-500 drop-shadow-sm">
                    Receive SMS notifications for critical events and urgent updates
                  </p>
                </div>
                <button
                  onClick={() => toggleNotification('sms')}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    notifications.sms ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4]' : 'bg-gradient-to-r from-[#4267A1]/60 to-[#5A7BC4]/60'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-lg ${
                      notifications.sms ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {notifications.sms && (
                <div className="mt-4 p-4 bg-slate-700 rounded-lg">
                  <label className="block text-sm font-medium text-slate-300 mb-2 drop-shadow-sm">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    className="w-full p-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
                  />
                  <p className="text-xs text-slate-500 mt-1 drop-shadow-sm">
                    Enter your phone number to receive SMS notifications for critical events
                  </p>
                </div>
              )}
            </div>

            {/* Desktop Notifications */}
            <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Monitor className="w-5 h-5 text-purple-400 drop-shadow-sm" />
                <h4 className="text-base font-medium text-white drop-shadow-sm">Desktop Notifications</h4>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-300 drop-shadow-sm">Browser notifications</p>
                  <p className="text-xs text-slate-500 drop-shadow-sm">
                    Show desktop notifications in your browser for real-time updates
                  </p>
                </div>
                <button
                  onClick={() => toggleNotification('desktop')}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    notifications.desktop ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4]' : 'bg-gradient-to-r from-[#4267A1]/60 to-[#5A7BC4]/60'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-lg ${
                      notifications.desktop ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {notifications.desktop && (
                <div className="mt-4 p-4 bg-slate-700 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-slate-300 mb-4">
                    <Bell className="w-4 h-4 drop-shadow-sm" />
                    <span className="drop-shadow-sm">Desktop notifications are enabled</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-4 drop-shadow-sm">
                    Make sure to allow notifications in your browser settings
                  </p>
                  
                  <div className="pl-6 border-l-2 border-purple-500/30">
                    <h4 className="text-sm font-medium text-slate-300 mb-3 drop-shadow-sm">Desktop Notification Types</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm text-slate-300 drop-shadow-sm">🔔 Notify on task complete</label>
                          <p className="text-xs text-slate-500 drop-shadow-sm">Get notified when tasks finish successfully</p>
                        </div>
                        <button
                          onClick={() => setNotifications({ 
                            taskComplete: !notifications.taskComplete 
                          })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            notifications.taskComplete ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4]' : 'bg-gradient-to-r from-[#4267A1]/60 to-[#5A7BC4]/60'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-lg ${
                            notifications.taskComplete ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm text-slate-300 drop-shadow-sm">🔔 Notify on input required</label>
                          <p className="text-xs text-slate-500 drop-shadow-sm">Get notified when Launch Pad needs your input</p>
                        </div>
                        <button
                          onClick={() => setNotifications({ 
                            requiresInput: !notifications.requiresInput 
                          })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            notifications.requiresInput ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4]' : 'bg-gradient-to-r from-[#4267A1]/60 to-[#5A7BC4]/60'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-lg ${
                            notifications.requiresInput ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case 'security':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-white drop-shadow-sm mb-2">Security Settings</h3>
              <p className="text-slate-300 drop-shadow-sm">Advanced security and access control options</p>
            </div>
            
            {/* Password Management */}
            <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-5 h-5 text-[#4267A1] drop-shadow-sm" />
                <h4 className="text-base font-medium text-white drop-shadow-sm">Password Management</h4>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2 drop-shadow-sm">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-[#4267A1] focus:ring-1 focus:ring-[#4267A1] transition-colors"
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2 drop-shadow-sm">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-[#4267A1] focus:ring-1 focus:ring-[#4267A1] transition-colors"
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2 drop-shadow-sm">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-[#4267A1] focus:ring-1 focus:ring-[#4267A1] transition-colors"
                    placeholder="Confirm new password"
                  />
                </div>
                <button className="w-full p-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 font-medium drop-shadow-sm">
                  Update Password
                </button>
              </div>
            </div>

            {/* Two-Factor Authentication */}
            <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-5 h-5 text-[#8A9150] drop-shadow-sm" />
                <h4 className="text-base font-medium text-white drop-shadow-sm">Two-Factor Authentication</h4>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-300 drop-shadow-sm">Enable 2FA</p>
                  <p className="text-xs text-slate-500 drop-shadow-sm">
                    Add an extra layer of security to your account
                  </p>
                </div>
                <button
                  onClick={handleMfaToggle}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    authSettings.mfaEnabled ? 'bg-gradient-to-r from-[#8A9150] to-[#A5B366]' : 'bg-gradient-to-r from-[#4267A1]/60 to-[#5A7BC4]/60'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-lg ${
                      authSettings.mfaEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {authSettings.mfaEnabled && (
                <div className="mt-4 p-4 bg-slate-700 rounded-lg">
                  <p className="text-sm text-[#8A9150] mb-2 drop-shadow-sm">✓ Two-factor authentication is enabled</p>
                  <button className="px-4 py-2 bg-gradient-to-r from-[#8A9150] to-[#A5B366] text-white rounded-lg hover:shadow-lg hover:shadow-[#8A9150]/25 transition-all duration-200 font-medium drop-shadow-sm text-sm">
                    View Recovery Codes
                  </button>
                </div>
              )}
            </div>

            {/* Session Management */}
            <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-5 h-5 text-[#5A7BC4] drop-shadow-sm" />
                <h4 className="text-base font-medium text-white drop-shadow-sm">Session Management</h4>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2 drop-shadow-sm">
                    Session Timeout (minutes)
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="5"
                      max="120"
                      value={authSettings.sessionTimeout}
                      onChange={(e) => handleSessionTimeoutChange(parseInt(e.target.value))}
                      className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-sm text-slate-400 w-12 text-right drop-shadow-sm">
                      {authSettings.sessionTimeout}m
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 drop-shadow-sm">
                    Automatically log out after this period of inactivity
                  </p>
                </div>

                <div className="p-4 bg-slate-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-300 drop-shadow-sm">Active Sessions</span>
                    <span className="text-xs text-slate-500 drop-shadow-sm">3 sessions</span>
                  </div>
                  <button className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:shadow-lg hover:shadow-red-500/25 transition-all duration-200 font-medium drop-shadow-sm text-sm">
                    Terminate All Other Sessions
                  </button>
                </div>
              </div>
            </div>

            {/* API Access Control */}
            <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-5 h-5 text-purple-400 drop-shadow-sm" />
                <h4 className="text-base font-medium text-white drop-shadow-sm">API Access Control</h4>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2 drop-shadow-sm">
                    Allowed Origins
                  </label>
                  <textarea
                    value={authSettings.allowedOrigins.join(', ')}
                    onChange={(e) => {
                      const origins = e.target.value.split(',').map(origin => origin.trim()).filter(Boolean);
                      setAuthSettings(prev => ({ ...prev, allowedOrigins: origins }));
                    }}
                    className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                    rows={3}
                    placeholder="https://tyler.com, https://app.tyler.com"
                  />
                  <p className="text-xs text-slate-500 mt-1 drop-shadow-sm">
                    Comma-separated list of allowed origins for API access
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300 drop-shadow-sm">Show API Keys</span>
                  <button
                    onClick={() => setShowApiKeys(!showApiKeys)}
                    className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors"
                  >
                    {showApiKeys ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {showApiKeys && (
                  <div className="p-4 bg-slate-700 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Key className="w-4 h-4 text-yellow-400 drop-shadow-sm" />
                      <span className="text-sm text-slate-300 drop-shadow-sm">API Key</span>
                    </div>
                    <code className="text-xs text-slate-400 font-mono break-all">
                      tyler_1234567890abcdef1234567890abcdef
                    </code>
                    <button className="mt-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:shadow-lg hover:shadow-yellow-500/25 transition-all duration-200 font-medium drop-shadow-sm text-sm">
                      Regenerate API Key
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 'integrations':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-white drop-shadow-sm mb-2">Integrations</h3>
              <p className="text-slate-300 drop-shadow-sm">Connect with external services and APIs</p>
            </div>

            {/* Tyler Technologies Services */}
            <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-6 rounded bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] flex items-center justify-center">
                  <span className="text-white text-xs font-bold drop-shadow-sm">T</span>
                </div>
                <h4 className="text-base font-medium text-white drop-shadow-sm">Tyler Technologies Services</h4>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#8A9150] drop-shadow-sm" />
                    <div>
                      <span className="text-white drop-shadow-sm">Tyler ERP</span>
                      <p className="text-xs text-slate-400 drop-shadow-sm">Connected and syncing</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-[#8A9150]/20 text-[#8A9150] rounded-full text-sm font-medium drop-shadow-sm">Active</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#8A9150] drop-shadow-sm" />
                    <div>
                      <span className="text-white drop-shadow-sm">Tyler CMS</span>
                      <p className="text-xs text-slate-400 drop-shadow-sm">Content management integration</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-[#8A9150]/20 text-[#8A9150] rounded-full text-sm font-medium drop-shadow-sm">Active</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 drop-shadow-sm" />
                    <div>
                      <span className="text-white drop-shadow-sm">Tyler Analytics</span>
                      <p className="text-xs text-slate-400 drop-shadow-sm">Requires authentication</p>
                    </div>
                  </div>
                  <button className="px-3 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-sm font-medium hover:bg-yellow-500/30 transition-colors drop-shadow-sm">
                    Configure
                  </button>
                </div>
              </div>
            </div>

            {/* Third-Party Integrations */}
            <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Plug className="w-5 h-5 text-purple-400 drop-shadow-sm" />
                <h4 className="text-base font-medium text-white drop-shadow-sm">Third-Party Services</h4>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-[#8A9150] drop-shadow-sm" />
                    <div>
                      <span className="text-white drop-shadow-sm">Slack</span>
                      <p className="text-xs text-slate-400 drop-shadow-sm">Team communication and notifications</p>
                    </div>
                  </div>
                  <button className="px-3 py-1 bg-[#8A9150]/20 text-[#8A9150] rounded-full text-sm font-medium hover:bg-[#8A9150]/30 transition-colors drop-shadow-sm">
                    Connect
                  </button>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#4267A1] drop-shadow-sm" />
                    <div>
                      <span className="text-white drop-shadow-sm">Microsoft Teams</span>
                      <p className="text-xs text-slate-400 drop-shadow-sm">Video conferencing and collaboration</p>
                    </div>
                  </div>
                  <button className="px-3 py-1 bg-[#4267A1]/20 text-[#4267A1] rounded-full text-sm font-medium hover:bg-[#4267A1]/30 transition-colors drop-shadow-sm">
                    Connect
                  </button>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Database className="w-5 h-5 text-orange-400 drop-shadow-sm" />
                    <div>
                      <span className="text-white drop-shadow-sm">External Database</span>
                      <p className="text-xs text-slate-400 drop-shadow-sm">Custom data source integration</p>
                    </div>
                  </div>
                  <button className="px-3 py-1 bg-orange-400/20 text-orange-400 rounded-full text-sm font-medium hover:bg-orange-400/30 transition-colors drop-shadow-sm">
                    Configure
                  </button>
                </div>
              </div>
            </div>

            {/* Webhook Management */}
            <div className="bg-gradient-to-br from-[#4267A1]/10 to-[#5A7BC4]/5 backdrop-blur-xl border border-[#4267A1]/20 rounded-xl hover:border-[#4267A1]/40 transition-all duration-300 p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-5 h-5 text-[#5A7BC4] drop-shadow-sm" />
                <h4 className="text-base font-medium text-white drop-shadow-sm">Webhook Management</h4>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2 drop-shadow-sm">
                    Webhook Endpoints
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                      <div>
                        <code className="text-xs text-slate-300 font-mono">https://api.tyler.com/webhooks/launch-pad</code>
                        <p className="text-xs text-slate-500 mt-1 drop-shadow-sm">Task completion notifications</p>
                      </div>
                      <button className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium hover:bg-red-500/30 transition-colors drop-shadow-sm">
                        Delete
                      </button>
                    </div>
                  </div>
                  
                  <button className="w-full mt-3 p-3 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-slate-500 hover:text-slate-300 transition-colors">
                    + Add New Webhook
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      case 'executive-assistance':
        return renderExecutiveAssistanceTab();
      case 'administration':
        return renderAdministrationTab();
      default:
        return renderProfileTab();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-[90vw] sm:w-[80vw] md:w-[70vw] lg:w-[60vw] xl:w-[900px] max-w-[90vw] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-l border-[#4267A1]/30 z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#4267A1]/30 flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-white to-gray-50 flex items-center justify-center shadow-lg border border-gray-200">
                  <img 
                    src="/tyler-technologies-logo.png" 
                    alt="Tyler Technologies Logo" 
                    className="w-10 h-10 object-contain drop-shadow-sm"
                    onError={(e) => console.error('Tyler Technologies logo failed to load:', e)}
                  />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white drop-shadow-sm">Launch Pad Settings</h2>
                  <p className="text-sm text-slate-300 drop-shadow-sm">Tyler Technologies Executive Portal</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 bg-slate-700/50 border border-slate-600/30 hover:bg-slate-600/50 text-slate-300 hover:text-white rounded-lg transition-all duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-1 min-h-0 overflow-hidden">
              {/* Tab Navigation */}
              <div className="w-64 flex-shrink-0 border-r border-[#4267A1]/30 p-4 overflow-y-auto">
                <nav className="space-y-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white shadow-lg'
                            : 'text-slate-300 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0 drop-shadow-sm" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate drop-shadow-sm">{tab.label}</div>
                          <div className="text-xs opacity-75 truncate">{tab.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="flex-1 min-w-0 p-6 overflow-y-auto">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="w-full max-w-4xl"
                >
                  {renderTabContent()}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LaunchPadSettings;
